import { observer } from 'mobx-react-lite'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'

// evertel
import {
    Modal, ModalBody, ModalHeader,
    Button, Row, Col, Icon, InfoBox, Text,
    NavItem, NavLink, Nav,
    RoomSearchSelect,
    TabContent, TabPane
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { SessionService } from '@evertel/session/SessionService'
import { CurrentUserController } from '@evertel/blue-user'
import { JoinRoomController } from '@evertel/room'
import { DepartmentController } from '@evertel/department'
import { VerifiedEmailDomainController } from '@evertel/shared/verified-email-domain'

import { JoinRoomByInviteCode } from './elements/JoinRoomByInviteCode'
import { JoinRoomRequestModal } from './JoinRoomRequestModal'
import { useCreateRoom } from './CreateRoomContext'
import { RoomListResultItem } from './RoomListResultItem'
import { SearchScope } from './JoinRoomContext'

interface JoinRoomModalProps {
    visible: boolean
    searchScope: SearchScope
    onClose: () => void
}


const JoinRoomModal: React.FC<JoinRoomModalProps> = observer(({
    visible,
    searchScope = 'department',
    onClose
}) => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    })

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        })
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const { openCreateRoom } = useCreateRoom()
    const navigate = useNavigate()

    const sessionService = useService(SessionService, [])
    const currentUserController = useService(CurrentUserController, [])
    const joinRoomController = useService(JoinRoomController, [])
    const departmentController = useService(DepartmentController, [sessionService.selectedDepartmentId])
    const verifiedEmailDomainController = useService(VerifiedEmailDomainController, [])
    const analytics = useService(Analytics, [])

    const [activeTab, setActiveTab] = useState<'department' | 'public'>(searchScope)
    const [showJoinByInviteCode, setShowByInviteCode] = useState(false)
    const [showExtraInfoModal, setShowExtraInfoModal] = useState(false)
    const [joinRequestRoomId, setJoinRequestRoomId] = useState(0)
    const [isGovernmentEmail, setIsGovernmentEmail] = useState(false)

    //not guest, but only verified departments count
    const hasDepartmentsAccessNotGuest = !!currentUserController.departmentsAccessNotGuest?.filter(i => i.isVerified).length

    const isMemberInSelectedDept = currentUserController.isMemberInDepartment(sessionService.selectedDepartmentId)

    useEffect(() => {
        setActiveTab(isMemberInSelectedDept ? searchScope : 'public')
    }, [searchScope, isMemberInSelectedDept])

    useEffect(() => {
        if (visible) {
            analytics.logView('join-room-modal', true)
            joinRoomController.init()
        } else {
            //occasionally this is left as true which causes it to open first on reopen and be stuck behind this modal
            setShowExtraInfoModal(false)
        }
    }, [visible])

    useEffect(() => {
        departmentController.init(sessionService.selectedDepartmentId)
    }, [sessionService.selectedDepartmentId])

    // only check gov email when user has no non-guest department role
    useEffect(() => {
        if (hasDepartmentsAccessNotGuest || !visible) return

        const email = currentUserController.user?.email
        if (!email) return

        const emailDomain = email.substring(email.lastIndexOf('@') + 1)

        verifiedEmailDomainController.getVerifiedEmailDomain(emailDomain)
            .then(domain => setIsGovernmentEmail(!!domain))
            .catch(() => setIsGovernmentEmail(false))
    }, [hasDepartmentsAccessNotGuest, visible, currentUserController.user, verifiedEmailDomainController])

    const onOpenExtraInfoModal = (roomId: number) => {
        setJoinRequestRoomId(roomId)
        setShowExtraInfoModal(true)
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            style={{ minHeight: 'calc(100vh - 4rem)' }}
            size="lg">
            <ModalHeader>
                <Row
                    className="w-100 me-2"
                    align="between"
                    valign="center">
                    <Text tag="h3" heavy>
                        Join a Room
                    </Text>
                    <Row>
                        <Button
                            color="primary"
                            outline
                            onClick={() => setShowByInviteCode(true)}>
                            Join by Invite Code
                        </Button>
                        {(currentUserController.canManage) &&
                            <Button
                                color="primary"
                                onClick={() => {
                                    onClose()
                                    openCreateRoom()
                                }}
                                className="ms-2">
                                <Icon
                                    name="plus-circle"
                                    className="me-2"
                                />
                                Create Room
                            </Button>
                        }
                    </Row>
                </Row>
            </ModalHeader>
            <ModalBody>
                {(showJoinByInviteCode) &&
                    <JoinRoomByInviteCode
                        onClose={() => setShowByInviteCode(false)}
                    />
                }
                {/* No department and no gov email — show notice */}
                {!isMemberInSelectedDept && !isGovernmentEmail ? (
                    <Col
                        align="center"
                        valign="center"
                        className="py-5">
                        <Icon
                            name="face-surprise"
                            color="muted"
                            size="3x"
                            className="mb-3"
                        />
                        <Text color="muted" className="mb-4">
                            {currentUserController.departmentsAccess?.length >= 1
                                ? 'You don\'t have access to join rooms in this agency'
                                : 'You do not belong to any agencies'
                            }
                        </Text>
                        <Button
                            color="primary"
                            onClick={() => {
                                onClose()
                                navigate('/account/departments/join')
                            }}>
                            Join Your Agency
                        </Button>
                    </Col>
                ) : (
                    <>
                        {/* Show tabs only when user has a non-guest department */}
                        <Nav
                            variant="underline"
                            role="tablist"
                        >
                            {isMemberInSelectedDept && (
                                <NavItem>
                                    <NavLink
                                        active={activeTab === 'department'}
                                        onClick={() => setActiveTab('department')}>
                                        <Text>
                                            {departmentController.shortName || 'Agency'} Rooms
                                        </Text>
                                    </NavLink>
                                </NavItem>
                            )}
                            <NavItem>
                                <NavLink
                                    active={activeTab === 'public'}
                                    onClick={() => setActiveTab('public')}>
                                    <Text>
                                        Public Rooms
                                    </Text>
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <TabContent>
                            <TabPane
                                role="tabpanel"
                                visible={isMemberInSelectedDept && activeTab === 'department'}
                            >
                                <Col valign="top" >
                                    {(isMemberInSelectedDept && activeTab === 'department') && (
                                        <RoomSearchSelect
                                            key={'agency-search'}
                                            searchOnly={true}
                                            searchScope="department"
                                            departmentId={sessionService.selectedDepartmentId}
                                            isMulti={false}
                                            className="select-inline search no-bg mt-2"
                                            menuIsOpen
                                            // @ts-expect-error string works
                                            maxMenuHeight="calc(80vh - 150px)"
                                            customComponents={{ Option: RoomListResultItem as any }}
                                            autoFocus
                                            customProps={{
                                                joinRoomController,
                                                showDept: true,
                                                onClose,
                                                onOpenExtraInfoModal
                                            }}
                                        />
                                    )}
                                </Col>
                            </TabPane>
                            <TabPane
                                role="tabpanel"
                                visible={activeTab === 'public'}
                            >
                                <Col valign="top" >
                                    {(activeTab === 'public') && (
                                        <>
                                            <InfoBox color="info" className="mt-2">
                                                Public rooms are viewable to anyone in Evertel who meets the room's access criteria. If you meet the criteria set by the agency that owns the room you
                                                will be given immediate access. If not, you can request to join and will gain access once approved by that agency.
                                            </InfoBox>
                                            <RoomSearchSelect
                                                key={'global-public-search'}
                                                searchOnly={true}
                                                searchScope="public"
                                                isMulti={false}
                                                className="select-inline mt-2 search no-bg"
                                                menuIsOpen
                                                // @ts-expect-error string works
                                                maxMenuHeight="calc(80vh - 250px)"
                                                customComponents={{ Option: RoomListResultItem as any }}
                                                autoFocus
                                                customProps={{
                                                    joinRoomController,
                                                    showDept: true,
                                                    onClose,
                                                    onOpenExtraInfoModal
                                                }}
                                            />
                                        </>
                                    )}
                                </Col>
                            </TabPane>
                        </TabContent>
                    </>
                )}

                <JoinRoomRequestModal
                    visible={showExtraInfoModal}
                    onClose={() => setShowExtraInfoModal(false)}
                    onSave={(body: any) => joinRoomController.requestToJoin(joinRequestRoomId, body)}
                />

            </ModalBody>
        </Modal>
    )
})

export { JoinRoomModal }
